import axios from 'axios';
import type { SettingsSchema } from '../types/settings';

/**
 * Carregador de assets por hostname.
 *
 * A marca é identificada por `window.location.hostname` e seus arquivos vivem
 * em `public/assets/<hostname>/`. Este é o ÚNICO módulo que lê o hostname —
 * `getAppHostname()` abaixo é o ponto único, e `src/api/client.ts` o consome
 * para montar o `x-caller-url`. Ver o invariante 5 do CLAUDE.md.
 */

const ABSOLUTE_ASSET_URL_RE = /^(?:[a-z]+:)?\/\//i;

/**
 * Hostname efetivo. `REACT_APP_HOSTNAME` permite testar a identidade de uma
 * marca em localhost sem mexer no /etc/hosts.
 */
export function getAppHostname(): string {
  return process.env.REACT_APP_HOSTNAME || window.location.hostname;
}

export function getAssetsBasePath(app?: string): string {
  const hostname = app || getAppHostname();
  return `/assets/${hostname}`;
}

/**
 * `settings.json` da marca.
 *
 * **Rejeita resposta que não seja JSON de objeto.** Tanto o fallback de SPA do
 * Apache (`.htaccess`) quanto o dev server do CRA respondem `200` com o
 * `index.html` para um caminho inexistente — e o axios, com
 * `silentJSONParsing`, devolve essa string sem lançar. Sem esta checagem, o
 * hostname sem pasta própria não caía no `catch` do `SettingsProvider`: o
 * HTML era espalhado dentro do objeto de settings, caractere a caractere.
 */
export async function getSettings(app?: string): Promise<Partial<SettingsSchema>> {
  const basePath = getAssetsBasePath(app);
  const response = await axios.get<Partial<SettingsSchema>>(`${basePath}/settings.json`);
  const data = response.data;

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(`settings.json inválido para ${app || getAppHostname()}`);
  }

  return data;
}

export async function getStaticContent(file: string, app?: string): Promise<string> {
  const basePath = getAssetsBasePath(app);
  const response = await axios.get<string>(`${basePath}/content/${file}`);
  return response.data;
}

export function getAssetUrl(path: string, app?: string): string {
  return `${getAssetsBasePath(app)}/${path}`;
}

/**
 * Resolve um caminho de asset: URLs absolutas, caminhos com `/` inicial e
 * data URIs passam direto; o resto é relativo à pasta da marca.
 */
export function resolveAssetUrl(path: string, app?: string): string {
  if (!path) return path;
  if (ABSOLUTE_ASSET_URL_RE.test(path) || path.startsWith('/') || path.startsWith('data:')) {
    return path;
  }
  return getAssetUrl(path, app);
}

/**
 * Injeta o `style.css` da marca (compilado a partir do `style.scss` pelo
 * `scripts/build-assets-styles.mjs`). Nunca rejeita: se a marca não tiver
 * folha própria, a aplicação segue com os defaults dos CSS custom properties.
 */
export function loadClientStylesheet(app?: string): Promise<void> {
  return new Promise((resolve) => {
    const basePath = getAssetsBasePath(app);
    const existing = document.getElementById('client-stylesheet');
    if (existing) existing.remove();

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${basePath}/style.css`;
    link.id = 'client-stylesheet';
    link.onload = () => resolve();
    link.onerror = () => {
      console.warn(`Sem folha de estilo própria para ${app || getAppHostname()}`);
      resolve();
    };
    document.head.appendChild(link);
  });
}
